/*
 * Admin screen: tabs (Settings, Home, Logs), message bar, and tab content.
 */

import React from "react";
import i18n from "../../../i18n/i18n.js";
import MessageBar from "../MessageBar.jsx";
import AdminHomeTab from "./AdminHomeTab.jsx";
import AdminSettingsTab from "./AdminSettingsTab.jsx";
import AdminLogsTab from "./AdminLogsTab.jsx";
import TemplateEditModal from "../modals/TemplateEditModal.jsx";
import { getTextDirection as getTextDirectionUtil } from "../../utils/templateUtils.js";

export default function AdminScreen({
  message,
  getMessageBarClass,
  activeTab,
  setActiveTab,
  getTextDirection: getTextDirectionProp,
  activeUserEmail,
  adminSettings,
  setAdminSettings,
  textareaRef,
  onSaveAdminSettings,
  loading,
  organization,
  loadingLogs,
  logsError,
  loadLogs,
  logs,
  setSelectedLog,
  selectedLog,
  editingTemplate,
  ...homeTabProps
}) {
  const getTextDirection = getTextDirectionProp || (() => getTextDirectionUtil(() => i18n.getLanguage()));

  return (
    <div className="p-4 max-w-md min-h-[500px] flex flex-col gap-4">
      {message && (
        <MessageBar message={message} getMessageBarClass={getMessageBarClass} />
      )}

      {/* Tabs */}
      <div dir="ltr" className={`flex border-b border-gray-200 ${getTextDirection() === "rtl" ? "flex-row-reverse" : ""}`}>
        <button
          onClick={() => setActiveTab("settings")}
          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === "settings"
              ? "text-[#0C75D1] border-b-2 border-[#0C75D1]"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          {i18n.t("labels.settings")}
        </button>
        <button
          onClick={() => setActiveTab("home")}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === "home"
              ? "text-[#0C75D1] border-b-2 border-[#0C75D1]"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          {i18n.t("labels.home")}
        </button>
        <button
          onClick={() => setActiveTab("logs")}
          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === "logs"
              ? "text-[#0C75D1] border-b-2 border-[#0C75D1]"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          {i18n.t("labels.logs")}
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "home" && (
        <AdminHomeTab getTextDirection={getTextDirection} {...homeTabProps} />
      )}
      {activeTab === "settings" && (
        <AdminSettingsTab
          getTextDirection={getTextDirection}
          adminSettings={adminSettings}
          setAdminSettings={setAdminSettings}
          textareaRef={textareaRef}
          onSaveSettings={onSaveAdminSettings}
          loading={loading}
        />
      )}
      {activeTab === "logs" && (
        <AdminLogsTab
          getTextDirection={getTextDirection}
          organization={organization}
          activeUserEmail={activeUserEmail}
          loadingLogs={loadingLogs}
          logsError={logsError}
          loadLogs={loadLogs}
          logs={logs}
          setSelectedLog={setSelectedLog}
          selectedLog={selectedLog}
        />
      )}

      <div className="border-t border-gray-200 pt-4 mt-auto">
        <div className="text-xs text-gray-500">
          <div>
            {i18n.t("common.admin")}: {activeUserEmail}
          </div>
        </div>
      </div>

      {/* Template Placeholder Editor Modal (admin home) */}
      {editingTemplate && (
        <TemplateEditModal
          editingTemplate={editingTemplate}
          logoUrls={homeTabProps.logoUrls}
          setLogoUrls={homeTabProps.setLogoUrls}
          setChangesSaved={homeTabProps.setChangesSaved}
          placeholderValues={homeTabProps.placeholderValues}
          onPlaceholderChange={homeTabProps.handlePlaceholderChange}
          selectedGroups={homeTabProps.selectedGroups}
          onCancel={homeTabProps.handleCancelEdit}
          onSavePlaceholders={homeTabProps.handleSavePlaceholders}
          loadingTemplates={homeTabProps.loadingTemplates}
          substitutePlaceholders={homeTabProps.substitutePlaceholders}
          replaceLogoPlaceholders={homeTabProps.replaceLogoPlaceholders}
          getTextDirection={getTextDirection}
        />
      )}
    </div>
  );
}
